import { useState, useEffect, useCallback, useRef } from "react";

const defaultState = {
  partnership: {
    name: "Our LLC",
    ein: "",
    businessStartDate: "2025-03-01",
    taxYear: "2025",
    partnerA: "Partner A",
    partnerB: "Partner B",
  },
  revenue: [],
  expenses: [],
  capitalContributions: [],
  distributions: [],
};

const normalizeAppData = (raw) => {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    partnership: {
      ...defaultState.partnership,
      ...(source.partnership && typeof source.partnership === "object" ? source.partnership : {}),
    },
    revenue: Array.isArray(source.revenue) ? source.revenue : [],
    expenses: Array.isArray(source.expenses) ? source.expenses : [],
    capitalContributions: Array.isArray(source.capitalContributions) ? source.capitalContributions : [],
    distributions: Array.isArray(source.distributions) ? source.distributions : [],
  };
};

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatBytes = (value) => {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const generateId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

const EXPENSE_CATEGORIES = [
  "Web Hosting",
  "Software / Subscriptions",
  "Domain / Email",
  "Podcast / Media",
  "Platform Fees",
  "Marketing / Advertising",
  "Office Supplies",
  "Professional Services",
  "Other",
];

// ─── Tiny Components ───
function Badge({ children, color = "green" }) {
  const colors = {
    green: "background: #10b981; color: #fff",
    amber: "background: #f59e0b; color: #fff",
    red: "background: #ef4444; color: #fff",
    slate: "background: #64748b; color: #fff",
    blue: "background: #3b82f6; color: #fff",
  };
  return (
    <span
      style={{
        ...parseStyle(colors[color]),
        padding: "2px 10px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function parseStyle(str) {
  return str.split(";").reduce((acc, rule) => {
    const [k, v] = rule.split(":").map((s) => s.trim());
    if (k && v) {
      const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      acc[camel] = v;
    }
    return acc;
  }, {});
}

// ─── Login Screen ───
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Login failed");
        setLoading(false);
        return;
      }
      onLogin(data.token, data.username, data.role || "partner");
    } catch {
      setError("Connection error");
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(145deg, #0c0f1a 0%, #141929 40%, #0e1420 100%)",
      fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <div style={{
        background: "rgba(30,41,59,0.7)",
        border: "1px solid rgba(148,163,184,0.1)",
        borderRadius: 16,
        padding: "40px",
        width: "100%",
        maxWidth: 360,
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 12,
          }}>⬡</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", letterSpacing: "-0.02em" }}>
            LLC Partner Tracker
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Sign in to continue</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>
              Username
            </label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              style={{
                background: "rgba(15,23,42,0.6)",
                border: "1px solid rgba(148,163,184,0.15)",
                borderRadius: 8,
                padding: "10px 14px",
                color: "#e2e8f0",
                fontSize: 14,
                outline: "none",
                width: "100%",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              style={{
                background: "rgba(15,23,42,0.6)",
                border: "1px solid rgba(148,163,184,0.15)",
                borderRadius: 8,
                padding: "10px 14px",
                color: "#e2e8f0",
                fontSize: 14,
                outline: "none",
                width: "100%",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
          </div>

          {error && (
            <div style={{
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.2)",
              borderRadius: 8,
              padding: "8px 12px",
              color: "#fca5a5",
              fontSize: 12,
              marginBottom: 14,
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              background: "linear-gradient(135deg, #3b82f6, #6366f1)",
              border: "none",
              color: "#fff",
              padding: "10px 18px",
              borderRadius: 8,
              cursor: loading ? "wait" : "pointer",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "inherit",
              width: "100%",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── App Entry Point ───
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("llc-token"));
  const [user, setUser] = useState(() => localStorage.getItem("llc-user"));
  const [role, setRole] = useState(() => localStorage.getItem("llc-role"));

  useEffect(() => {
    if (!token || role) return;
    fetch("/api/me", {
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    }).then(async (res) => {
      if (res.status === 401) {
        localStorage.removeItem("llc-token");
        localStorage.removeItem("llc-user");
        localStorage.removeItem("llc-role");
        setToken(null);
        setUser(null);
        setRole(null);
        return;
      }
      const data = await res.json();
      if (data?.role) {
        localStorage.setItem("llc-role", data.role);
        setRole(data.role);
      }
    }).catch(() => {});
  }, [token, role]);

  const handleLogin = (newToken, username, nextRole) => {
    localStorage.setItem("llc-token", newToken);
    localStorage.setItem("llc-user", username);
    localStorage.setItem("llc-role", nextRole || "partner");
    setToken(newToken);
    setUser(username);
    setRole(nextRole || "partner");
  };

  const handleLogout = () => {
    localStorage.removeItem("llc-token");
    localStorage.removeItem("llc-user");
    localStorage.removeItem("llc-role");
    setToken(null);
    setUser(null);
    setRole(null);
  };

  if (!token) return <LoginScreen onLogin={handleLogin} />;
  return <LLCTracker token={token} user={user} role={role || "partner"} onLogout={handleLogout} />;
}

// ─── Main App ───
function LLCTracker({ token, user, role, onLogout }) {
  const [data, setData] = useState(defaultState);
  const [tab, setTab] = useState("dashboard");
  const [editingPartnership, setEditingPartnership] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState("loading");
  const [hasHydrated, setHasHydrated] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState("");
  const [dashboardYearFrom, setDashboardYearFrom] = useState("all");
  const [dashboardYearTo, setDashboardYearTo] = useState("all");

  const lastUpdatedAt = useRef(0);
  const skipNextSave = useRef(false);

  const api = useCallback(async (method, url, body) => {
    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.status === 401) { onLogout(); return null; }
      return res.json();
    } catch {
      return null;
    }
  }, [token, onLogout]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setInitialLoadError("");
    setSyncStatus("loading");

    const result = await api("GET", "/api/data");
    if (!result || !result.data) {
      setHasHydrated(false);
      setInitialLoadError("Could not load saved data from the server.");
      setSyncStatus("error");
      setLoading(false);
      return;
    }

    setData(normalizeAppData(result.data));
    lastUpdatedAt.current = result.updatedAt || 0;
    skipNextSave.current = true;
    setHasHydrated(true);
    setLoading(false);
    setSyncStatus("synced");
  }, [api]);

  // Load initial data from server
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Save on data change (debounced 500ms)
  useEffect(() => {
    if (loading || !hasHydrated) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    setSyncStatus("saving");
    const timer = setTimeout(() => {
      api("PUT", "/api/data", { data }).then((result) => {
        if (result && typeof result.updatedAt === "number") {
          lastUpdatedAt.current = result.updatedAt;
          setSyncStatus("synced");
        } else {
          setSyncStatus("error");
        }
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [data, api, loading, hasHydrated]);

  // Poll for sync every 5 seconds
  useEffect(() => {
    if (loading || !hasHydrated) return;
    const interval = setInterval(async () => {
      const result = await api("GET", "/api/sync");
      if (result && result.updatedAt > lastUpdatedAt.current) {
        const fresh = await api("GET", "/api/data");
        if (fresh) {
          lastUpdatedAt.current = fresh.updatedAt;
          skipNextSave.current = true;
          setData(normalizeAppData(fresh.data));
          setSyncStatus("synced");
        }
      } else if (!result) {
        setSyncStatus("error");
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [api, loading, hasHydrated]);

  const update = useCallback((key, val) => {
    setData((prev) => ({ ...prev, [key]: typeof val === "function" ? val(prev[key]) : val }));
  }, []);

  const extractYear = useCallback((dateValue) => {
    const value = String(dateValue || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    return Number(value.slice(0, 4));
  }, []);

  const dashboardYearOptions = (() => {
    const years = new Set();
    [
      ...(Array.isArray(data.revenue) ? data.revenue : []),
      ...(Array.isArray(data.expenses) ? data.expenses : []),
      ...(Array.isArray(data.capitalContributions) ? data.capitalContributions : []),
      ...(Array.isArray(data.distributions) ? data.distributions : []),
    ].forEach((entry) => {
      const year = extractYear(entry?.date);
      if (year) years.add(year);
    });
    return Array.from(years).sort((a, b) => a - b).map((y) => String(y));
  })();

  const filterByDashboardYear = useCallback((entries) => {
    const safe = Array.isArray(entries) ? entries : [];
    if (dashboardYearFrom === "all" && dashboardYearTo === "all") return safe;
    const fromYear = dashboardYearFrom === "all" ? -Infinity : Number(dashboardYearFrom);
    const toYear = dashboardYearTo === "all" ? Infinity : Number(dashboardYearTo);
    const low = Math.min(fromYear, toYear);
    const high = Math.max(fromYear, toYear);
    return safe.filter((entry) => {
      const year = extractYear(entry?.date);
      if (year === null) return false;
      return year >= low && year <= high;
    });
  }, [dashboardYearFrom, dashboardYearTo, extractYear]);

  // ─── Computed values ───
  const totalRevenue = data.revenue.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const startupExpenses = data.expenses.filter((e) => e.type === "startup").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const operatingExpenses = data.expenses.filter((e) => e.type === "operating").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalExpenses = startupExpenses + operatingExpenses;
  const netIncome = totalRevenue - totalExpenses;

  const partnerAExpenseContributions = data.expenses.filter((e) => e.paidBy === "A").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const partnerBExpenseContributions = data.expenses.filter((e) => e.paidBy === "B").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const partnerACapitalContributions = data.capitalContributions.filter((c) => c.partner === "A").reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const partnerBCapitalContributions = data.capitalContributions.filter((c) => c.partner === "B").reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const partnerAContributions = partnerAExpenseContributions + partnerACapitalContributions;
  const partnerBContributions = partnerBExpenseContributions + partnerBCapitalContributions;

  const distA = data.distributions.filter((d) => d.partner === "A").reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const distB = data.distributions.filter((d) => d.partner === "B").reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);

  const incomeShare = netIncome / 2;
  const basisA = partnerAContributions + incomeShare - distA;
  const basisB = partnerBContributions + incomeShare - distB;

  // ─── Dashboard year-range values ───
  const dashboardRevenueEntries = filterByDashboardYear(data.revenue);
  const dashboardExpenseEntries = filterByDashboardYear(data.expenses);
  const dashboardCapitalEntries = filterByDashboardYear(data.capitalContributions);
  const dashboardDistributionEntries = filterByDashboardYear(data.distributions);

  const dashboardTotalRevenue = dashboardRevenueEntries.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const dashboardStartupExpenses = dashboardExpenseEntries.filter((e) => e.type === "startup").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const dashboardOperatingExpenses = dashboardExpenseEntries.filter((e) => e.type === "operating").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const dashboardTotalExpenses = dashboardStartupExpenses + dashboardOperatingExpenses;
  const dashboardNetIncome = dashboardTotalRevenue - dashboardTotalExpenses;
  const dashboardIncomeShare = dashboardNetIncome / 2;

  const dashboardPartnerAExpenseContributions = dashboardExpenseEntries.filter((e) => e.paidBy === "A").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const dashboardPartnerBExpenseContributions = dashboardExpenseEntries.filter((e) => e.paidBy === "B").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const dashboardPartnerACapitalContributions = dashboardCapitalEntries.filter((c) => c.partner === "A").reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const dashboardPartnerBCapitalContributions = dashboardCapitalEntries.filter((c) => c.partner === "B").reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const dashboardPartnerAContributions = dashboardPartnerAExpenseContributions + dashboardPartnerACapitalContributions;
  const dashboardPartnerBContributions = dashboardPartnerBExpenseContributions + dashboardPartnerBCapitalContributions;

  const dashboardDistA = dashboardDistributionEntries.filter((d) => d.partner === "A").reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const dashboardDistB = dashboardDistributionEntries.filter((d) => d.partner === "B").reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const dashboardBasisA = dashboardPartnerAContributions + dashboardIncomeShare - dashboardDistA;
  const dashboardBasisB = dashboardPartnerBContributions + dashboardIncomeShare - dashboardDistB;

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "\u25E9" },
    { id: "revenue", label: "Revenue", icon: "\u2197" },
    { id: "capital", label: "Capital", icon: "\u25CB" },
    { id: "expenses", label: "Expenses", icon: "\u2199" },
    { id: "distributions", label: "Distributions", icon: "\u21C4" },
    { id: "basis", label: "Basis Tracking", icon: "\u2261" },
    { id: "taxsummary", label: "Tax Summary", icon: "\u2726" },
    { id: "tickets", label: role === "admin" ? "Tickets Admin" : "Support", icon: "\u2709" },
  ];

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(145deg, #0c0f1a 0%, #141929 40%, #0e1420 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#94a3b8", fontSize: 14, fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
        Loading...
      </div>
    );
  }

  if (!hasHydrated) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(145deg, #0c0f1a 0%, #141929 40%, #0e1420 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#e2e8f0",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        padding: 20,
      }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
        <div style={{
          background: "rgba(30,41,59,0.7)",
          border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 12,
          padding: 20,
          width: "100%",
          maxWidth: 460,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Server sync required</div>
          <div style={{ color: "#fca5a5", fontSize: 13, marginBottom: 14 }}>
            {initialLoadError || "Could not load your saved data."}
          </div>
          <button onClick={loadInitialData} style={btnPrimary}>Retry Load</button>
        </div>
      </div>
    );
  }

  const syncIndicator = {
    synced: { text: "Synced", color: "#10b981" },
    saving: { text: "Saving...", color: "#f59e0b" },
    loading: { text: "Loading...", color: "#94a3b8" },
    error: { text: "Sync error", color: "#ef4444" },
  }[syncStatus] || { text: "Synced", color: "#10b981" };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(145deg, #0c0f1a 0%, #141929 40%, #0e1420 100%)",
      fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
      color: "#e2e8f0",
      padding: "0",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.06))",
        borderBottom: "1px solid rgba(148,163,184,0.08)",
        padding: "20px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "12px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 700, color: "#fff",
          }}>{"\u2B21"}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em" }}>
              {data.partnership.name}
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", fontFamily: "'DM Mono', monospace" }}>
              Tax Year {data.partnership.taxYear} · 50/50 Partnership
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Sync status */}
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncIndicator.color }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%",
              background: syncIndicator.color,
            }} />
            {syncIndicator.text}
          </div>
          {syncStatus === "error" && (
            <button
              onClick={loadInitialData}
              style={{
                ...btnGhost,
                padding: "4px 10px",
                fontSize: 11,
                borderColor: "rgba(239,68,68,0.25)",
                color: "#fca5a5",
              }}
            >
              Retry
            </button>
          )}
          {/* User badge */}
          <div style={{
            background: "rgba(139,92,246,0.12)",
            border: "1px solid rgba(139,92,246,0.2)",
            padding: "4px 10px",
            borderRadius: 6,
            fontSize: 11,
            color: "#a78bfa",
            fontWeight: 500,
            textTransform: "capitalize",
          }}>
            {user} {role === "admin" ? "(admin)" : ""}
          </div>
          <button
            onClick={() => setEditingPartnership(!editingPartnership)}
            style={{
              background: "rgba(148,163,184,0.1)",
              border: "1px solid rgba(148,163,184,0.15)",
              color: "#94a3b8",
              padding: "6px 14px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {"\u2699"} Settings
          </button>
          <button
            onClick={onLogout}
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.2)",
              color: "#f87171",
              padding: "6px 14px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Partnership Settings */}
      {editingPartnership && (
        <div style={{
          margin: "16px 28px",
          background: "rgba(30,41,59,0.7)",
          border: "1px solid rgba(148,163,184,0.1)",
          borderRadius: 12,
          padding: "20px",
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Partnership Settings</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {[
              ["LLC Name", "name"],
              ["EIN", "ein"],
              ["Business Start Date", "businessStartDate"],
              ["Tax Year", "taxYear"],
              ["Partner A Name", "partnerA"],
              ["Partner B Name", "partnerB"],
            ].map(([label, key]) => (
              <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
                <input
                  value={data.partnership[key]}
                  onChange={(e) => update("partnership", { ...data.partnership, [key]: e.target.value })}
                  type={key === "businessStartDate" ? "date" : "text"}
                  style={{
                    background: "rgba(15,23,42,0.6)",
                    border: "1px solid rgba(148,163,184,0.15)",
                    borderRadius: 8,
                    padding: "8px 12px",
                    color: "#e2e8f0",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
              </label>
            ))}
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
            <button onClick={() => setEditingPartnership(false)} style={btnPrimary}>Done</button>
            <button
              onClick={() => { if (window.confirm("Reset ALL data? This cannot be undone.")) { setData(normalizeAppData(defaultState)); } }}
              style={{ ...btnGhost, color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}
            >
              Reset All Data
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: "flex",
        gap: 2,
        padding: "12px 28px 0",
        overflowX: "auto",
        flexWrap: "wrap",
      }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 16px",
              background: tab === t.id ? "rgba(59,130,246,0.12)" : "transparent",
              border: "none",
              borderBottom: tab === t.id ? "2px solid #3b82f6" : "2px solid transparent",
              color: tab === t.id ? "#93c5fd" : "#64748b",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: tab === t.id ? 600 : 400,
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
          >
            <span style={{ fontSize: 15 }}>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: "20px 28px 40px" }}>
        {tab === "dashboard" && (
          <DashboardTab
            {...{
              totalRevenue: dashboardTotalRevenue,
              startupExpenses: dashboardStartupExpenses,
              operatingExpenses: dashboardOperatingExpenses,
              totalExpenses: dashboardTotalExpenses,
              netIncome: dashboardNetIncome,
              incomeShare: dashboardIncomeShare,
              partnerAContributions: dashboardPartnerAContributions,
              partnerBContributions: dashboardPartnerBContributions,
              partnerAExpenseContributions: dashboardPartnerAExpenseContributions,
              partnerBExpenseContributions: dashboardPartnerBExpenseContributions,
              partnerACapitalContributions: dashboardPartnerACapitalContributions,
              partnerBCapitalContributions: dashboardPartnerBCapitalContributions,
              distA: dashboardDistA,
              distB: dashboardDistB,
              basisA: dashboardBasisA,
              basisB: dashboardBasisB,
              data,
              yearOptions: dashboardYearOptions,
              yearFrom: dashboardYearFrom,
              yearTo: dashboardYearTo,
              setYearFrom: setDashboardYearFrom,
              setYearTo: setDashboardYearTo,
            }}
          />
        )}
        {tab === "revenue" && <RevenueTab revenue={data.revenue} update={update} />}
        {tab === "capital" && <CapitalContributionsTab contributions={data.capitalContributions} update={update} partnership={data.partnership} />}
        {tab === "expenses" && <ExpensesTab expenses={data.expenses} update={update} partnership={data.partnership} />}
        {tab === "distributions" && <DistributionsTab distributions={data.distributions} update={update} partnership={data.partnership} />}
        {tab === "basis" && (
          <BasisTab
            {...{
              partnerAContributions, partnerBContributions,
              partnerAExpenseContributions, partnerBExpenseContributions,
              partnerACapitalContributions, partnerBCapitalContributions,
              incomeShare, distA, distB, basisA, basisB, data,
            }}
          />
        )}
        {tab === "taxsummary" && (
          <TaxSummaryTab {...{ totalRevenue, startupExpenses, operatingExpenses, totalExpenses, netIncome, incomeShare, distA, distB, data }} />
        )}
        {tab === "tickets" && <TicketsTab api={api} token={token} user={user} role={role} partnership={data.partnership} />}
      </div>
    </div>
  );
}

// ─── Shared Styles ───
const btnPrimary = {
  background: "linear-gradient(135deg, #3b82f6, #6366f1)",
  border: "none",
  color: "#fff",
  padding: "8px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
};
const btnGhost = {
  background: "transparent",
  border: "1px solid rgba(148,163,184,0.2)",
  color: "#94a3b8",
  padding: "8px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "inherit",
};
const cardStyle = {
  background: "rgba(30,41,59,0.5)",
  border: "1px solid rgba(148,163,184,0.08)",
  borderRadius: 14,
  padding: "20px",
};
const inputStyle = {
  background: "rgba(15,23,42,0.6)",
  border: "1px solid rgba(148,163,184,0.15)",
  borderRadius: 8,
  padding: "8px 12px",
  color: "#e2e8f0",
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};
const labelStyle = {
  fontSize: 11,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 4,
  display: "block",
};

// ─── Dashboard ───
function DashboardTab({
  totalRevenue, startupExpenses, operatingExpenses, totalExpenses, netIncome, incomeShare,
  partnerAContributions, partnerBContributions,
  partnerAExpenseContributions, partnerBExpenseContributions,
  partnerACapitalContributions, partnerBCapitalContributions,
  distA, distB, basisA, basisB, data,
  yearOptions, yearFrom, yearTo, setYearFrom, setYearTo,
}) {
  const statCards = [
    { label: "Gross Revenue", value: formatCurrency(totalRevenue), color: "#10b981" },
    { label: "Total Expenses", value: formatCurrency(totalExpenses), color: "#f59e0b", sub: `Startup: ${formatCurrency(startupExpenses)} · Operating: ${formatCurrency(operatingExpenses)}` },
    { label: "Net Income", value: formatCurrency(netIncome), color: netIncome >= 0 ? "#10b981" : "#ef4444" },
    { label: "Per Partner (50%)", value: formatCurrency(incomeShare), color: "#8b5cf6" },
  ];

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "#cbd5e1", fontWeight: 600 }}>Dashboard Year Range</div>
          <div style={{ display: "flex", alignItems: "end", gap: 8, flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={labelStyle}>From Year</span>
              <select value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} style={{ ...inputStyle, width: 120 }}>
                <option value="all">All</option>
                {(yearOptions || []).map((y) => <option key={`from-${y}`} value={y}>{y}</option>)}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={labelStyle}>To Year</span>
              <select value={yearTo} onChange={(e) => setYearTo(e.target.value)} style={{ ...inputStyle, width: 120 }}>
                <option value="all">All</option>
                {(yearOptions || []).map((y) => <option key={`to-${y}`} value={y}>{y}</option>)}
              </select>
            </label>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14, marginBottom: 24 }}>
        {statCards.map((s, i) => (
          <div key={i} style={{ ...cardStyle, borderLeft: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Mono', monospace" }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Partner Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
        {[
          {
            name: data.partnership.partnerA,
            contrib: partnerAContributions,
            expenseContrib: partnerAExpenseContributions,
            capitalContrib: partnerACapitalContributions,
            dist: distA,
            basis: basisA,
            tag: "A",
          },
          {
            name: data.partnership.partnerB,
            contrib: partnerBContributions,
            expenseContrib: partnerBExpenseContributions,
            capitalContrib: partnerBCapitalContributions,
            dist: distB,
            basis: basisB,
            tag: "B",
          },
        ].map((p) => (
          <div key={p.tag} style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: p.tag === "A" ? "linear-gradient(135deg, #3b82f6, #60a5fa)" : "linear-gradient(135deg, #8b5cf6, #a78bfa)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, color: "#fff",
              }}>{p.tag}</div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { l: "Capital In", v: p.capitalContrib, c: "#0ea5e9" },
                { l: "Expense Paid", v: p.expenseContrib, c: "#3b82f6" },
                { l: "Total Contrib", v: p.contrib, c: "#60a5fa" },
                { l: "Income Share", v: incomeShare, c: "#10b981" },
                { l: "Distributions", v: p.dist, c: "#f59e0b" },
                { l: "End Basis", v: p.basis, c: "#8b5cf6" },
              ].map((item, i) => (
                <div key={i}>
                  <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.l}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: item.c, fontFamily: "'DM Mono', monospace" }}>{formatCurrency(item.v)}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Revenue Tab ───
function RevenueTab({ revenue, update }) {
  const emptyForm = { date: "", source: "", amount: "", method: "", notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const saveRevenue = () => {
    if (!form.amount) return;
    if (editingId) {
      update("revenue", (prev) => prev.map((r) => (
        r.id === editingId ? { ...form, id: editingId } : r
      )));
    } else {
      update("revenue", (prev) => [...prev, { ...form, id: generateId() }]);
    }
    setForm(emptyForm);
    setEditingId(null);
  };

  const removeRevenue = (id) => update("revenue", (prev) => prev.filter((r) => r.id !== id));
  const editRevenue = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || "",
      source: entry.source || "",
      amount: entry.amount || "",
      method: entry.method || "",
      notes: entry.notes || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const total = revenue.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{editingId ? "Edit Revenue Entry" : "Add Revenue Entry"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          <div><span style={labelStyle}>Date</span><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} /></div>
          <div><span style={labelStyle}>Client / Source</span><input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Client name" style={inputStyle} /></div>
          <div><span style={labelStyle}>Amount ($)</span><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={inputStyle} /></div>
          <div>
            <span style={labelStyle}>Method</span>
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} style={inputStyle}>
              <option value="">Select...</option>
              <option value="PayPal">PayPal</option>
              <option value="Stripe">Stripe</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Venmo">Venmo</option>
              <option value="Check">Check</option>
              <option value="Cash">Cash</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div><span style={labelStyle}>Notes</span><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" style={inputStyle} /></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
          <button onClick={saveRevenue} style={btnPrimary}>{editingId ? "Save Changes" : "+ Add Revenue"}</button>
          {editingId && <button onClick={cancelEdit} style={btnGhost}>Cancel</button>}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Revenue Log</div>
          <Badge color="green">Total: {formatCurrency(total)}</Badge>
        </div>
        {revenue.length === 0 ? (
          <div style={{ color: "#475569", textAlign: "center", padding: 30, fontSize: 13 }}>No revenue entries yet. Add your first one above.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                  {["Date", "Source", "Amount", "Method", "Notes", "", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {revenue.map((r) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid rgba(148,163,184,0.05)" }}>
                    <td style={tdStyle}>{r.date}</td>
                    <td style={tdStyle}>{r.source}</td>
                    <td style={{ ...tdStyle, color: "#10b981", fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{formatCurrency(r.amount)}</td>
                    <td style={tdStyle}>{r.method}</td>
                    <td style={{ ...tdStyle, color: "#64748b" }}>{r.notes}</td>
                    <td style={tdStyle}><button onClick={() => editRevenue(r)} style={miniBtn}>Edit</button></td>
                    <td style={tdStyle}><button onClick={() => removeRevenue(r.id)} style={delBtn}>{"\u2715"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const tdStyle = { padding: "10px", whiteSpace: "nowrap" };
const miniBtn = {
  background: "transparent",
  border: "1px solid rgba(148,163,184,0.25)",
  color: "#93c5fd",
  cursor: "pointer",
  borderRadius: 6,
  fontSize: 11,
  padding: "3px 8px",
  fontFamily: "inherit",
};
const delBtn = { background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14, padding: "4px 8px", fontFamily: "inherit" };

// ─── Expenses Tab ───
function ExpensesTab({ expenses, update, partnership }) {
  const emptyForm = { date: "", vendor: "", amount: "", description: "", type: "operating", taxBucket: "operating", paidBy: "A", category: "Other" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const saveExpense = () => {
    if (!form.amount) return;
    const normalized = {
      ...form,
      taxBucket: form.taxBucket || (form.type === "startup" ? "startup" : "operating"),
      type: form.taxBucket === "operating" ? "operating" : "startup",
    };
    if (editingId) {
      update("expenses", (prev) => prev.map((e) => (
        e.id === editingId ? { ...normalized, id: editingId } : e
      )));
    } else {
      update("expenses", (prev) => [...prev, { ...normalized, id: generateId() }]);
    }
    setForm(emptyForm);
    setEditingId(null);
  };

  const removeExpense = (id) => update("expenses", (prev) => prev.filter((e) => e.id !== id));
  const editExpense = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || "",
      vendor: entry.vendor || "",
      amount: entry.amount || "",
      description: entry.description || "",
      type: entry.type || "operating",
      taxBucket: entry.taxBucket || (entry.type === "startup" ? "startup" : "operating"),
      paidBy: entry.paidBy || "A",
      category: entry.category || "Other",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startupTotal = expenses.filter((e) => e.type === "startup").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const operatingTotal = expenses.filter((e) => e.type === "operating").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const aTotal = expenses.filter((e) => e.paidBy === "A").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const bTotal = expenses.filter((e) => e.paidBy === "B").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const bizTotal = expenses.filter((e) => e.paidBy === "BIZ").reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
          {editingId ? "Edit Expense" : "Add Expense (treated as Partner Contribution)"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          <div><span style={labelStyle}>Date Paid</span><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} /></div>
          <div><span style={labelStyle}>Vendor</span><input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Company name" style={inputStyle} /></div>
          <div><span style={labelStyle}>Amount ($)</span><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={inputStyle} /></div>
          <div><span style={labelStyle}>Description</span><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Business purpose" style={inputStyle} /></div>
          <div>
            <span style={labelStyle}>Category</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <span style={labelStyle}>Tax Bucket</span>
            <select value={form.taxBucket} onChange={(e) => setForm({ ...form, taxBucket: e.target.value })} style={inputStyle}>
              <option value="operating">Operating</option>
              <option value="startup">Startup</option>
              <option value="organizational">Organizational</option>
            </select>
          </div>
          <div>
            <span style={labelStyle}>Legacy Type</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} style={inputStyle}>
              <option value="startup">Startup (pre-business)</option>
              <option value="operating">Operating</option>
            </select>
          </div>
          <div>
            <span style={labelStyle}>Paid By</span>
            <select value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })} style={inputStyle}>
              <option value="A">{partnership.partnerA}</option>
              <option value="B">{partnership.partnerB}</option>
              <option value="BIZ">Business (BoA)</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
          <button onClick={saveExpense} style={btnPrimary}>{editingId ? "Save Changes" : "+ Add Expense"}</button>
          {editingId && <button onClick={cancelEdit} style={btnGhost}>Cancel</button>}
        </div>
      </div>

      {/* Summary Badges */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Badge color="amber">Total: {formatCurrency(startupTotal + operatingTotal)}</Badge>
        <Badge color="red">Startup: {formatCurrency(startupTotal)}</Badge>
        <Badge color="slate">Operating: {formatCurrency(operatingTotal)}</Badge>
        <Badge color="blue">{partnership.partnerA}: {formatCurrency(aTotal)}</Badge>
        <Badge color="blue">{partnership.partnerB}: {formatCurrency(bTotal)}</Badge>
        {bizTotal > 0 && <Badge color="green">Business (BoA): {formatCurrency(bizTotal)}</Badge>}
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Expense & Contribution Log</div>
        {expenses.length === 0 ? (
          <div style={{ color: "#475569", textAlign: "center", padding: 30, fontSize: 13 }}>No expenses yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                  {["Date", "Vendor", "Amount", "Category", "Tax Bucket", "Type", "Paid By", "Description", "", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} style={{ borderBottom: "1px solid rgba(148,163,184,0.05)" }}>
                    <td style={tdStyle}>{e.date}</td>
                    <td style={tdStyle}>{e.vendor}</td>
                    <td style={{ ...tdStyle, color: "#f59e0b", fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{formatCurrency(e.amount)}</td>
                    <td style={tdStyle}>{e.category}</td>
                    <td style={tdStyle}>{e.taxBucket || (e.type === "startup" ? "startup" : "operating")}</td>
                    <td style={tdStyle}><Badge color={e.type === "startup" ? "red" : "slate"}>{e.type}</Badge></td>
                    <td style={tdStyle}>{e.paidBy === "A" ? partnership.partnerA : e.paidBy === "B" ? partnership.partnerB : "Business (BoA)"}</td>
                    <td style={{ ...tdStyle, color: "#64748b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{e.description}</td>
                    <td style={tdStyle}><button onClick={() => editExpense(e)} style={miniBtn}>Edit</button></td>
                    <td style={tdStyle}><button onClick={() => removeExpense(e.id)} style={delBtn}>{"\u2715"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Capital Contributions Tab ───
function CapitalContributionsTab({ contributions, update, partnership }) {
  const emptyForm = { date: "", partner: "A", amount: "", source: "Personal transfer", memo: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const saveContribution = () => {
    if (!form.amount) return;
    if (editingId) {
      update("capitalContributions", (prev) => prev.map((c) => (
        c.id === editingId ? { ...form, id: editingId } : c
      )));
    } else {
      update("capitalContributions", (prev) => [...prev, { ...form, id: generateId() }]);
    }
    setForm(emptyForm);
    setEditingId(null);
  };

  const removeContribution = (id) => update("capitalContributions", (prev) => prev.filter((c) => c.id !== id));
  const editContribution = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || "",
      partner: entry.partner || "A",
      amount: entry.amount || "",
      source: entry.source || "Personal transfer",
      memo: entry.memo || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const total = contributions.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const totalA = contributions.filter((c) => c.partner === "A").reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const totalB = contributions.filter((c) => c.partner === "B").reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 8, background: "rgba(14,165,233,0.08)", borderColor: "rgba(14,165,233,0.2)" }}>
        <div style={{ fontSize: 12, color: "#7dd3fc", lineHeight: 1.6 }}>
          Capital contributions are owner money put into the business (for example: personal funds deposited into business bank). These increase basis and do not hit expenses.
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, marginTop: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{editingId ? "Edit Capital Contribution" : "Add Capital Contribution"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          <div><span style={labelStyle}>Date</span><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} /></div>
          <div>
            <span style={labelStyle}>Partner</span>
            <select value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} style={inputStyle}>
              <option value="A">{partnership.partnerA}</option>
              <option value="B">{partnership.partnerB}</option>
            </select>
          </div>
          <div><span style={labelStyle}>Amount ($)</span><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={inputStyle} /></div>
          <div>
            <span style={labelStyle}>Source</span>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} style={inputStyle}>
              <option value="Personal transfer">Personal transfer</option>
              <option value="Cash deposit">Cash deposit</option>
              <option value="Check">Check</option>
              <option value="Wire">Wire</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div><span style={labelStyle}>Memo</span><input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="e.g. initial bank deposit" style={inputStyle} /></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
          <button onClick={saveContribution} style={btnPrimary}>{editingId ? "Save Changes" : "+ Add Contribution"}</button>
          {editingId && <button onClick={cancelEdit} style={btnGhost}>Cancel</button>}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Badge color="blue">Total: {formatCurrency(total)}</Badge>
        <Badge color="slate">{partnership.partnerA}: {formatCurrency(totalA)}</Badge>
        <Badge color="slate">{partnership.partnerB}: {formatCurrency(totalB)}</Badge>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Capital Contribution Log</div>
        {contributions.length === 0 ? (
          <div style={{ color: "#475569", textAlign: "center", padding: 30, fontSize: 13 }}>No capital contributions recorded yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                  {["Date", "Partner", "Amount", "Source", "Memo", "", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contributions.map((c) => (
                  <tr key={c.id} style={{ borderBottom: "1px solid rgba(148,163,184,0.05)" }}>
                    <td style={tdStyle}>{c.date}</td>
                    <td style={tdStyle}>{c.partner === "A" ? partnership.partnerA : partnership.partnerB}</td>
                    <td style={{ ...tdStyle, color: "#38bdf8", fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{formatCurrency(c.amount)}</td>
                    <td style={tdStyle}>{c.source}</td>
                    <td style={{ ...tdStyle, color: "#64748b" }}>{c.memo}</td>
                    <td style={tdStyle}><button onClick={() => editContribution(c)} style={miniBtn}>Edit</button></td>
                    <td style={tdStyle}><button onClick={() => removeContribution(c.id)} style={delBtn}>{"\u2715"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Distributions Tab ───
function DistributionsTab({ distributions, update, partnership }) {
  const emptyForm = { date: "", partner: "A", amount: "", memo: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const saveDist = () => {
    if (!form.amount) return;
    if (editingId) {
      update("distributions", (prev) => prev.map((d) => (
        d.id === editingId ? { ...form, id: editingId } : d
      )));
    } else {
      update("distributions", (prev) => [...prev, { ...form, id: generateId() }]);
    }
    setForm(emptyForm);
    setEditingId(null);
  };

  const removeDist = (id) => update("distributions", (prev) => prev.filter((d) => d.id !== id));
  const editDist = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || "",
      partner: entry.partner || "A",
      amount: entry.amount || "",
      memo: entry.memo || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 8, background: "rgba(139,92,246,0.06)", borderColor: "rgba(139,92,246,0.15)" }}>
        <div style={{ fontSize: 12, color: "#a78bfa", lineHeight: 1.6 }}>
          <strong>Note:</strong> Partner "paybacks" are treated as <strong>cash distributions</strong>, not expenses. Record them here — they reduce partner basis but are not deducted on Form 1065. Distributions in {partnership.taxYear} go on that year's K-1 Box 19.
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, marginTop: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
          {editingId ? "Edit Distribution" : "Add Distribution"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
          <div><span style={labelStyle}>Date</span><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} /></div>
          <div>
            <span style={labelStyle}>Partner</span>
            <select value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} style={inputStyle}>
              <option value="A">{partnership.partnerA}</option>
              <option value="B">{partnership.partnerB}</option>
            </select>
          </div>
          <div><span style={labelStyle}>Amount ($)</span><input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={inputStyle} /></div>
          <div><span style={labelStyle}>Memo</span><input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="e.g. distribution" style={inputStyle} /></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
          <button onClick={saveDist} style={btnPrimary}>{editingId ? "Save Changes" : "+ Add Distribution"}</button>
          {editingId && <button onClick={cancelEdit} style={btnGhost}>Cancel</button>}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Distribution Log</div>
        {distributions.length === 0 ? (
          <div style={{ color: "#475569", textAlign: "center", padding: 30, fontSize: 13 }}>No distributions recorded.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                  {["Date", "Partner", "Amount", "Memo", "", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {distributions.map((d) => (
                  <tr key={d.id} style={{ borderBottom: "1px solid rgba(148,163,184,0.05)" }}>
                    <td style={tdStyle}>{d.date}</td>
                    <td style={tdStyle}>{d.partner === "A" ? partnership.partnerA : partnership.partnerB}</td>
                    <td style={{ ...tdStyle, color: "#a78bfa", fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{formatCurrency(d.amount)}</td>
                    <td style={{ ...tdStyle, color: "#64748b" }}>{d.memo}</td>
                    <td style={tdStyle}><button onClick={() => editDist(d)} style={miniBtn}>Edit</button></td>
                    <td style={tdStyle}><button onClick={() => removeDist(d.id)} style={delBtn}>{"\u2715"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Basis Tracking Tab ───
function BasisTab({
  partnerAContributions, partnerBContributions,
  partnerAExpenseContributions, partnerBExpenseContributions,
  partnerACapitalContributions, partnerBCapitalContributions,
  incomeShare, distA, distB, basisA, basisB, data,
}) {
  const Row = ({ label, a, b, color = "#e2e8f0", bold = false }) => (
    <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.06)" }}>
      <td style={{ padding: "10px 14px", fontWeight: bold ? 700 : 400, fontSize: bold ? 14 : 13 }}>{label}</td>
      <td style={{ padding: "10px 14px", textAlign: "right", color, fontFamily: "'DM Mono', monospace", fontWeight: bold ? 700 : 500, fontSize: bold ? 15 : 13 }}>{formatCurrency(a)}</td>
      <td style={{ padding: "10px 14px", textAlign: "right", color, fontFamily: "'DM Mono', monospace", fontWeight: bold ? 700 : 500, fontSize: bold ? 15 : 13 }}>{formatCurrency(b)}</td>
    </tr>
  );

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 8, background: "rgba(59,130,246,0.06)", borderColor: "rgba(59,130,246,0.15)" }}>
        <div style={{ fontSize: 12, color: "#93c5fd", lineHeight: 1.6 }}>
          <strong>Partner Basis Worksheet</strong> — Each partner's basis starts at $0, increases by contributions and allocated income, and decreases by distributions. Distributions are generally not taxable unless they exceed basis.
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
              <th style={{ textAlign: "left", padding: "8px 14px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}></th>
              <th style={{ textAlign: "right", padding: "8px 14px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{data.partnership.partnerA}</th>
              <th style={{ textAlign: "right", padding: "8px 14px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{data.partnership.partnerB}</th>
            </tr>
          </thead>
          <tbody>
            <Row label="Starting Basis (Year 1)" a={0} b={0} color="#64748b" />
            <Row label="+ Capital Contributions (deposits/funding)" a={partnerACapitalContributions} b={partnerBCapitalContributions} color="#0ea5e9" />
            <Row label="+ Expense Contributions (paid personally)" a={partnerAExpenseContributions} b={partnerBExpenseContributions} color="#3b82f6" />
            <Row label="+ Total Contributions" a={partnerAContributions} b={partnerBContributions} color="#60a5fa" />
            <Row label="+ Allocated Income (50%)" a={Math.max(incomeShare, 0)} b={Math.max(incomeShare, 0)} color="#10b981" />
            {incomeShare < 0 && <Row label={"\u2212 Allocated Loss (50%)"} a={Math.abs(incomeShare)} b={Math.abs(incomeShare)} color="#ef4444" />}
            <Row label={"\u2212 Distributions"} a={distA} b={distB} color="#f59e0b" />
            <Row label={`End of ${data.partnership.taxYear} Basis`} a={basisA} b={basisB} color="#8b5cf6" bold />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tax Summary Tab ───
function TaxSummaryTab({ totalRevenue, startupExpenses, operatingExpenses, totalExpenses, netIncome, incomeShare, distA, distB, data }) {
  // Build category breakdown for Line 20 statement
  const categoryTotals = {};
  data.expenses.forEach((e) => {
    const key = e.type === "startup" ? `Startup costs (business began ${data.partnership.businessStartDate})` : e.category;
    categoryTotals[key] = (categoryTotals[key] || 0) + (parseFloat(e.amount) || 0);
  });

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16, background: "rgba(16,185,129,0.06)", borderColor: "rgba(16,185,129,0.15)" }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "#6ee7b7" }}>{"\u2726"} Form 1065 Quick Reference</div>
        <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7 }}>
          This summarizes the key numbers for your Form 1065 partnership return. This is a reference tool — consult a tax professional if needed.
        </div>
      </div>

      {/* 1065 Page 1 */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Form 1065 — Page 1</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            <SummaryRow label="Line 1a – Gross Receipts" value={formatCurrency(totalRevenue)} />
            <SummaryRow label="Line 21 – Other Deductions" value={formatCurrency(totalExpenses)} />
            <SummaryRow label="Line 23 – Ordinary Business Income (Loss)" value={formatCurrency(netIncome)} bold color={netIncome >= 0 ? "#10b981" : "#ef4444"} />
          </tbody>
        </table>
      </div>

      {/* Line 20 Statement */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Line 20 Other Deductions — Attachment Statement</div>
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 14 }}>
          "Statement for Form 1065, Line 20 – Other Deductions (Tax Year {data.partnership.taxYear})"
        </div>
        {Object.keys(categoryTotals).length === 0 ? (
          <div style={{ color: "#475569", fontSize: 13 }}>Add expenses to generate this statement.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                <SummaryRow key={cat} label={cat} value={formatCurrency(amt)} />
              ))}
              <SummaryRow label="Total (must match Line 21)" value={formatCurrency(totalExpenses)} bold color="#f59e0b" />
            </tbody>
          </table>
        )}
      </div>

      {/* Schedule K */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Schedule K (Partnership Totals)</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            <SummaryRow label="Line 1 – Ordinary Business Income" value={formatCurrency(netIncome)} />
          </tbody>
        </table>
      </div>

      {/* K-1 Summary */}
      <div style={{ ...cardStyle }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Schedule K-1 (Per Partner — 50% each)</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
              <th style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>K-1 Field</th>
              <th style={{ textAlign: "right", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{data.partnership.partnerA}</th>
              <th style={{ textAlign: "right", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{data.partnership.partnerB}</th>
            </tr>
          </thead>
          <tbody>
            <K1Row label="Box 1 – Ordinary Business Income" a={incomeShare} b={incomeShare} />
            <K1Row label="Box 14, Code A – SE Earnings" a={incomeShare} b={incomeShare} />
            <K1Row label="Box 19 – Distributions" a={distA} b={distB} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tickets Tab ───
function TicketsTab({ api, token, user, role, partnership }) {
  const isAdmin = role === "admin";
  const [tickets, setTickets] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsBusy, setReportsBusy] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [reportsNotice, setReportsNotice] = useState("");
  const [reportYear, setReportYear] = useState(() => (partnership?.taxYear || new Date().getFullYear().toString()));
  const [finalizeReport, setFinalizeReport] = useState(false);
  const [acknowledgeExclusions, setAcknowledgeExclusions] = useState(false);
  const [lastExclusionSummary, setLastExclusionSummary] = useState(null);
  const [backups, setBackups] = useState([]);
  const [backupConfig, setBackupConfig] = useState({ autoBackupHours: null, retentionDays: null, backupDir: "" });
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupError, setBackupError] = useState("");

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [newTicket, setNewTicket] = useState({ kind: "issue", subject: "", message: "" });
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadTickets = useCallback(async () => {
    const result = await api("GET", "/api/tickets");
    if (!result || !Array.isArray(result.tickets)) {
      setListError("Could not load tickets.");
      setListLoading(false);
      return;
    }
    setTickets(result.tickets);
    setListError("");
    setListLoading(false);
  }, [api]);

  const loadBackups = useCallback(async () => {
    if (!isAdmin) return;
    setBackupLoading(true);
    const result = await api("GET", "/api/admin/backups");
    if (!result || !Array.isArray(result.backups)) {
      setBackupError("Could not load backups.");
      setBackupLoading(false);
      return;
    }
    setBackups(result.backups);
    setBackupConfig({
      autoBackupHours: result.autoBackupHours,
      retentionDays: result.retentionDays,
      backupDir: result.backupDir || "",
    });
    setBackupError("");
    setBackupLoading(false);
  }, [api, isAdmin]);

  const loadReports = useCallback(async () => {
    if (!isAdmin) return;
    setReportsLoading(true);
    const result = await api("GET", "/api/admin/reports");
    if (!result || !Array.isArray(result.reports)) {
      setReportsError("Could not load tax package files.");
      setReportsLoading(false);
      return;
    }
    setReports(result.reports);
    setReportsError("");
    setReportsLoading(false);
  }, [api, isAdmin]);

  const loadTicketDetail = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setDetailLoading(true);
    const result = await api("GET", `/api/tickets/${ticketId}`);
    if (!result || !result.ticket) {
      setDetailError("Could not load this ticket.");
      setDetailLoading(false);
      return;
    }
    setSelectedTicket(result.ticket);
    setMessages(Array.isArray(result.messages) ? result.messages : []);
    setDetailError("");
    setDetailLoading(false);
  }, [api]);

  useEffect(() => {
    loadTickets();
    const interval = setInterval(loadTickets, 15000);
    return () => clearInterval(interval);
  }, [loadTickets]);

  useEffect(() => {
    if (!isAdmin) return;
    loadBackups();
    const interval = setInterval(loadBackups, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, loadBackups]);

  useEffect(() => {
    if (!isAdmin) return;
    loadReports();
    const interval = setInterval(loadReports, 45000);
    return () => clearInterval(interval);
  }, [isAdmin, loadReports]);

  useEffect(() => {
    if (!selectedTicketId) return;
    loadTicketDetail(selectedTicketId);
  }, [selectedTicketId, loadTicketDetail]);

  const openTicket = (ticketId) => {
    setSelectedTicketId(ticketId);
    setReplyText("");
  };

  const refreshCurrent = async () => {
    await loadTickets();
    if (isAdmin) await loadBackups();
    if (isAdmin) await loadReports();
    if (selectedTicketId) await loadTicketDetail(selectedTicketId);
  };

  const createManualBackup = async () => {
    if (!isAdmin) return;
    setBackupBusy(true);
    const result = await api("POST", "/api/admin/backups", { reason: "manual_ui" });
    setBackupBusy(false);
    if (!result || result.error) {
      setBackupError(result?.error || "Could not create backup.");
      return;
    }
    await loadBackups();
  };

  const downloadBackup = async (name) => {
    if (!isAdmin) return;
    setBackupError("");
    try {
      const res = await fetch(`/api/admin/backups/${encodeURIComponent(name)}/download`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.error || "Could not download backup.");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setBackupError(err.message || "Could not download backup.");
    }
  };

  const createTaxPackage = async () => {
    if (!isAdmin) return;
    if (!/^\d{4}$/.test(reportYear.trim())) {
      setReportsError("Tax year must be a 4-digit year (YYYY).");
      return;
    }

    setReportsBusy(true);
    setReportsNotice("");
    const result = await api("POST", "/api/admin/reports/generate", {
      taxYear: reportYear.trim(),
      finalize: finalizeReport,
      acknowledgeExclusions,
    });
    setReportsBusy(false);
    if (!result || result.error) {
      if (result?.exclusions) setLastExclusionSummary(result.exclusions);
      setReportsError(result?.error || "Could not generate tax package.");
      return;
    }

    if (result.exclusions) setLastExclusionSummary(result.exclusions);
    const warningCount = Array.isArray(result.warnings) ? result.warnings.length : 0;
    const exclusionCount = result.exclusionCount ?? (result.exclusions?.totalCount || 0);
    setReportsNotice(`Generated ${result.status || "draft"} package for ${result.taxYear}. Warnings: ${warningCount}. Exclusions: ${exclusionCount}.`);
    setReportsError("");
    await loadReports();
  };

  const downloadReport = async (name) => {
    if (!isAdmin) return;
    setReportsError("");
    try {
      const res = await fetch(`/api/admin/reports/${encodeURIComponent(name)}/download`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.error || "Could not download report file.");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setReportsError(err.message || "Could not download report file.");
    }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    const subject = newTicket.subject.trim();
    const message = newTicket.message.trim();
    if (!subject || !message) return;

    setSubmitting(true);
    const result = await api("POST", "/api/tickets", {
      subject,
      kind: newTicket.kind,
      message,
    });
    setSubmitting(false);

    if (!result || result.error) {
      setListError(result?.error || "Could not create ticket.");
      return;
    }

    setNewTicket({ kind: "issue", subject: "", message: "" });
    await loadTickets();
    if (result.ticketId) setSelectedTicketId(result.ticketId);
  };

  const submitReply = async () => {
    const message = replyText.trim();
    if (!selectedTicketId || !message) return;

    setSubmitting(true);
    const result = await api("POST", `/api/tickets/${selectedTicketId}/replies`, { message });
    setSubmitting(false);

    if (!result || result.error) {
      setDetailError(result?.error || "Could not send reply.");
      return;
    }

    setReplyText("");
    await refreshCurrent();
  };

  const setStatus = async (status) => {
    if (!selectedTicketId || !isAdmin) return;
    setSubmitting(true);
    const result = await api("PATCH", `/api/tickets/${selectedTicketId}`, { status });
    setSubmitting(false);

    if (!result || result.error) {
      setDetailError(result?.error || "Could not update status.");
      return;
    }

    await refreshCurrent();
  };

  const statusColor = (status) => {
    if (status === "open") return "amber";
    if (status === "in_progress") return "blue";
    if (status === "closed") return "green";
    return "slate";
  };

  return (
    <div>
      {isAdmin && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Tax Package (Form 1065 + K-1 Aid)</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={loadReports} style={btnGhost}>Refresh Files</button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={labelStyle}>Tax Year</span>
              <input
                value={reportYear}
                onChange={(e) => setReportYear(e.target.value)}
                placeholder="YYYY"
                maxLength={4}
                style={{ ...inputStyle, width: 120 }}
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#cbd5e1", fontSize: 12 }}>
              <input
                type="checkbox"
                checked={finalizeReport}
                onChange={(e) => setFinalizeReport(e.target.checked)}
              />
              Finalize package
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 12 }}>
              <input
                type="checkbox"
                checked={acknowledgeExclusions}
                onChange={(e) => setAcknowledgeExclusions(e.target.checked)}
              />
              Acknowledge exclusions (review marker)
            </label>
            <button
              type="button"
              onClick={createTaxPackage}
              disabled={reportsBusy}
              style={{ ...btnPrimary, opacity: reportsBusy ? 0.7 : 1 }}
            >
              {reportsBusy ? "Generating..." : "Generate Tax Package"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 10 }}>
            Generates `.md` and `.json` snapshots based on entries dated in the selected tax year.
          </div>
          {reportsError && <div style={{ color: "#fca5a5", fontSize: 12, marginBottom: 10 }}>{reportsError}</div>}
          {reportsNotice && <div style={{ color: "#86efac", fontSize: 12, marginBottom: 10 }}>{reportsNotice}</div>}
          {lastExclusionSummary && (
            <div style={{ marginBottom: 10, fontSize: 12, color: "#fbbf24" }}>
              Exclusions: {lastExclusionSummary.totalCount || 0} record(s), {formatCurrency(lastExclusionSummary.totalAmount || 0)} total.
              {Array.isArray(lastExclusionSummary.byReason) && lastExclusionSummary.byReason.length > 0 && (
                <div style={{ marginTop: 4, color: "#94a3b8" }}>
                  {lastExclusionSummary.byReason.slice(0, 4).map((r) => `${r.reason}: ${r.count}`).join(" | ")}
                </div>
              )}
              <div style={{ marginTop: 4, color: "#fca5a5" }}>
                Finalize is blocked until exclusions are resolved.
              </div>
            </div>
          )}
          {reportsLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading report files...</div>
          ) : reports.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>No tax package files yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                    {["File", "Created", "Size", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.name} style={{ borderBottom: "1px solid rgba(148,163,184,0.05)" }}>
                      <td style={{ ...tdStyle, maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis" }}>{report.name}</td>
                      <td style={tdStyle}>{formatDateTime(report.createdAt)}</td>
                      <td style={tdStyle}>{formatBytes(report.sizeBytes)}</td>
                      <td style={tdStyle}>
                        <button type="button" onClick={() => downloadReport(report.name)} style={miniBtn}>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Database Backups</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={loadBackups} style={btnGhost}>Refresh Backups</button>
              <button
                type="button"
                onClick={createManualBackup}
                disabled={backupBusy}
                style={{ ...btnPrimary, opacity: backupBusy ? 0.7 : 1 }}
              >
                {backupBusy ? "Creating..." : "Create Backup"}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>
            Auto every {backupConfig.autoBackupHours ?? "-"}h · Retention {backupConfig.retentionDays ?? "-"} days
          </div>
          {backupError && <div style={{ color: "#fca5a5", fontSize: 12, marginBottom: 10 }}>{backupError}</div>}
          {backupLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading backups...</div>
          ) : backups.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>No backup files found.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.1)" }}>
                    {["File", "Created", "Size", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup) => (
                    <tr key={backup.name} style={{ borderBottom: "1px solid rgba(148,163,184,0.05)" }}>
                      <td style={{ ...tdStyle, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>{backup.name}</td>
                      <td style={tdStyle}>{formatDateTime(backup.createdAt)}</td>
                      <td style={tdStyle}>{formatBytes(backup.sizeBytes)}</td>
                      <td style={tdStyle}>
                        <button type="button" onClick={() => downloadBackup(backup.name)} style={miniBtn}>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!isAdmin && (
        <form onSubmit={submitTicket} style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
            Report Issue or Request Feature
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <span style={labelStyle}>Type</span>
              <select
                value={newTicket.kind}
                onChange={(e) => setNewTicket((prev) => ({ ...prev, kind: e.target.value }))}
                style={inputStyle}
              >
                <option value="issue">Issue</option>
                <option value="feature">Feature Request</option>
                <option value="question">Question</option>
              </select>
            </div>
            <div>
              <span style={labelStyle}>Subject</span>
              <input
                value={newTicket.subject}
                onChange={(e) => setNewTicket((prev) => ({ ...prev, subject: e.target.value }))}
                placeholder="Short summary"
                style={inputStyle}
                maxLength={160}
              />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Details</span>
            <textarea
              value={newTicket.message}
              onChange={(e) => setNewTicket((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="What happened? What feature do you want?"
              rows={4}
              style={{ ...inputStyle, resize: "vertical", minHeight: 96 }}
              maxLength={4000}
            />
          </div>
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <button type="submit" disabled={submitting} style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Submitting..." : "Submit Ticket"}
            </button>
            <button type="button" onClick={refreshCurrent} style={btnGhost}>Refresh</button>
          </div>
        </form>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 360px) 1fr", gap: 14 }}>
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{isAdmin ? "All Tickets" : "My Tickets"}</div>
            <button type="button" onClick={refreshCurrent} style={{ ...btnGhost, padding: "6px 10px", fontSize: 11 }}>Refresh</button>
          </div>
          {listError && <div style={{ color: "#fca5a5", fontSize: 12, marginBottom: 10 }}>{listError}</div>}
          {listLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading tickets...</div>
          ) : tickets.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>No tickets yet.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {tickets.map((ticket) => {
                const unread = isAdmin ? ticket.unreadForAdmin : ticket.unreadForUser;
                return (
                  <button
                    key={ticket.id}
                    onClick={() => openTicket(ticket.id)}
                    style={{
                      textAlign: "left",
                      background: selectedTicketId === ticket.id ? "rgba(59,130,246,0.12)" : "rgba(15,23,42,0.45)",
                      border: "1px solid rgba(148,163,184,0.12)",
                      borderRadius: 10,
                      padding: 10,
                      color: "#e2e8f0",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        #{ticket.id} {ticket.subject}
                      </div>
                      <Badge color={statusColor(ticket.status)}>{ticket.status.replace("_", " ")}</Badge>
                    </div>
                    <div style={{ marginTop: 5, fontSize: 11, color: "#94a3b8" }}>
                      {ticket.kind} · {ticket.createdBy} · {formatDateTime(ticket.updatedAt)}
                    </div>
                    {unread > 0 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: "#f59e0b" }}>
                        {unread} unread update{unread > 1 ? "s" : ""}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={cardStyle}>
          {!selectedTicketId ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Select a ticket to view the thread.</div>
          ) : detailLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading ticket...</div>
          ) : (
            <div>
              {detailError && <div style={{ color: "#fca5a5", fontSize: 12, marginBottom: 10 }}>{detailError}</div>}
              {selectedTicket && (
                <div style={{ marginBottom: 12, borderBottom: "1px solid rgba(148,163,184,0.08)", paddingBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>#{selectedTicket.id} {selectedTicket.subject}</div>
                    <Badge color={statusColor(selectedTicket.status)}>{selectedTicket.status.replace("_", " ")}</Badge>
                    <Badge color="slate">{selectedTicket.kind}</Badge>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
                    Created by {selectedTicket.createdBy} · Last update {formatDateTime(selectedTicket.updatedAt)}
                  </div>
                  {isAdmin && (
                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" onClick={() => setStatus("open")} style={btnGhost}>Mark Open</button>
                      <button type="button" onClick={() => setStatus("in_progress")} style={btnGhost}>In Progress</button>
                      <button type="button" onClick={() => setStatus("closed")} style={btnGhost}>Close</button>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "grid", gap: 10, marginBottom: 12, maxHeight: 380, overflowY: "auto", paddingRight: 4 }}>
                {messages.map((msg) => {
                  const mine = msg.author === user;
                  return (
                    <div
                      key={msg.id}
                      style={{
                        background: mine ? "rgba(59,130,246,0.12)" : "rgba(15,23,42,0.45)",
                        border: "1px solid rgba(148,163,184,0.12)",
                        borderRadius: 10,
                        padding: 10,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: mine ? "#93c5fd" : "#a78bfa" }}>
                          {msg.author}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{formatDateTime(msg.createdAt)}</div>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: "#e2e8f0", whiteSpace: "pre-wrap" }}>{msg.body}</div>
                    </div>
                  );
                })}
                {messages.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>No messages yet.</div>}
              </div>

              <div>
                <span style={labelStyle}>Reply</span>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a reply..."
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 84 }}
                  maxLength={4000}
                />
                <div style={{ marginTop: 10 }}>
                  <button type="button" onClick={submitReply} disabled={submitting || !replyText.trim()} style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? "Sending..." : "Send Reply"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, bold = false, color = "#e2e8f0" }) {
  return (
    <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.06)" }}>
      <td style={{ padding: "10px", fontWeight: bold ? 700 : 400 }}>{label}</td>
      <td style={{ padding: "10px", textAlign: "right", fontFamily: "'DM Mono', monospace", fontWeight: bold ? 700 : 500, color, fontSize: bold ? 15 : 13 }}>{value}</td>
    </tr>
  );
}

function K1Row({ label, a, b }) {
  return (
    <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.06)" }}>
      <td style={{ padding: "10px" }}>{label}</td>
      <td style={{ padding: "10px", textAlign: "right", fontFamily: "'DM Mono', monospace", fontWeight: 500, color: "#93c5fd" }}>{formatCurrency(a)}</td>
      <td style={{ padding: "10px", textAlign: "right", fontFamily: "'DM Mono', monospace", fontWeight: 500, color: "#a78bfa" }}>{formatCurrency(b)}</td>
    </tr>
  );
}
